import { Observable, Observer, ReplaySubject, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';
import { AuthorizationManager } from '../security/authorization-manager';
import { NodeRequestOptions } from './node-connection';
import { PowerShell, PowerShellCommand } from './powershell';
import {
    WebsocketStream,
    WebsocketStreamConnectionState,
    WebsocketStreamDataRequestState,
    WebsocketStreamDataState,
    WebsocketStreamDataTarget,
    WebsocketStreamHandler,
    WebsocketStreamName,
    WebsocketStreamProcessor
} from './websocket-stream';

/**
 * PowerShell result object including an error.
 */
export interface PowerShellResult {
    /**
     * The runspace pool instance ID.
     */
    sessionId: string;

    /**
     * Completed state.
     */
    completed: 'True' | 'False';

    /**
     * Progress data.
     */
    progress?: any[];

    /**
     * Results data.
     */
    results?: any[];

    /**
     * Errors data.
     */
    errors?: any[];

    /**
     * Warning data.
     */
    warning?: any[];
}

/**
 * PowerShell Stream options.
 */
export interface PowerShellStreamOptions extends NodeRequestOptions {
    /**
     * Partial data response.
     * (default is waiting for completion.)
     */
    partial?: boolean;

    /**
     * Close the session each time, so it gets priority.
     */
    close?: boolean;

    /**
     * Override Query ID so it can request cancel.
     */
    queryId?: string;

    /**
     * Buffering time period by milliseconds.
     */
    bufferTime?: number;

    /**
     * Buffering count.
     */
    bufferCount?: number;
}

/**
 * The request packet of Cim Stream to the gateway.
 */
export interface PowerShellStreamRequest {
    /**
     * The identification string (auto generated or supplied as queryId option.)
     */
    id: string;

    /**
     * The stream target
     */
    target: WebsocketStreamDataTarget;

    /**
     * The date request state.
     */
    requestState: WebsocketStreamDataRequestState;

    /**
     * The name of PowerShell module.
     */
    module: string;

    /**
     * The command.
     */
    command: string;

    /**
     * The parameters.
     */
    parameters: any;

    /**
     * The script.
     */
    script: string;

    /**
     * The Cim stream options.
     */
    options: PowerShellStreamOptions;
}

/**
 * Cim stream response.
 */
export interface PowerShellStreamResponse {
    /**
     * The identification string (auto generated.)
     */
    id: string;

    /**
     * Web socket data stream state.
     */
    state: WebsocketStreamDataState;

    /**
     * Index number of response for the original request.
     */
    index: number;

    /**
     * The PowerShell result.
     */
    response: PowerShellResult;
}

export interface PowerShellStreamMonitorSetContext<T> {
    nodeName: string;
    command: PowerShellCommand;
    options?: PowerShellStreamOptions;
    data: T;
}

/**
 * Set of monitors for pre and post process of powershell stream.
 */
export interface PowerShellStreamMonitorSet<T> {
    name: string;
    preMonitor: (nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions) =>
        Observable<PowerShellStreamMonitorSetContext<any>>;
    successMonitor: (result: T, context: PowerShellStreamMonitorSetContext<any>) => Observable<T>;
    errorMonitor: (error: any, context: PowerShellStreamMonitorSetContext<any>) => Observable<T>;
}

/**
 * PowerShell Processor interface.
 */
class PowerShellProcessor extends WebsocketStreamProcessor<PowerShellResult, PowerShellStreamOptions>  {
    /**
     * * Holding result if waitCompleted option is specified for multiple instances.
     */
    declare public response?: PowerShellResult;
}

/**
 * PowerShell stream queue.
 */
interface PowerShellStreamQueue {
    /**
     * Number of active request.
     */
    outstandingCount: number;

    /**
     * Holding requests.
     */
    pendingRequests: PowerShellStreamRequest[];
}

/**
 * The PowerShell stream class.
 */
export class PowerShellStream implements WebsocketStreamHandler {
    private static readonly logSourceName = 'PowerShellStream';
    /**
     * The collection of set of monitors.
     */
    private static monitorSets: PowerShellStreamMonitorSet<any>[] = [];
    private static maxRunPerNode = 5;
    private processors: Map<string /* id */, PowerShellProcessor> = new Map();
    private queues: Map<string /* nodeName */, PowerShellStreamQueue> = new Map();
    private strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.WebsocketStream.PowerShellStream;

    /**
     * Register the set of monitors.
     *
     * @param monitorSet The set of monitors.
     */
    public static registerMonitorSet(monitorSet: PowerShellStreamMonitorSet<any>): void {
        const found = PowerShellStream.monitorSets.find(monitors => monitors.name === monitorSet.name);
        if (found) {
            return;
        }

        PowerShellStream.monitorSets.push(monitorSet);
    }

    /**
     * Unregister the set of monitors.
     *
     * @param name The name of set of monitors.
     * @returns boolean true if unregistered the named set.
     */
    public static unregisterMonitors(name: string): boolean {
        const found = PowerShellStream.monitorSets.find(monitors => monitors.name === name);
        if (found) {
            PowerShellStream.monitorSets.remove(found);
            return true;
        }

        return false;
    }

    /**
     * Initializes a new instance of the PowerShellStream class.
     *
     * @param websocketStream the websocket stream object.
     * @param authorizationManager the authorization manager object.
     */
    constructor(private websocketStream: WebsocketStream, private authorizationManager: AuthorizationManager) {
        websocketStream.registerProcessor(WebsocketStreamName.PowerShellStreamName, this);
    }

    /**
     * PowerShell script run.
     *
     * @param nodeName the node name.
     * @param script the script to run.
     * @param options the options for this request.
     * @return Observable<PowerShellResult> the query observable.
     */
    public run(
            nodeName: string,
            commandOrScript: string | PowerShellCommand,
            options?: PowerShellStreamOptions): Observable<PowerShellResult> {
        const command = PowerShell.getPowerShellCommand(commandOrScript);
        return this.monitorCreateRequest(nodeName, command, PowerShell.newEndpointOptions(options));
    }

    /**
     * Cancel active powershell script.
     * Result response comes back to the original query to end.
     *
     * @param nodeName the node name.
     * @param id the id of original request specified as options.queryId.
     */
    public cancel(nodeName: string, id: string, options?: PowerShellStreamOptions): void {
        const target = this.getTarget(nodeName, PowerShell.newEndpointOptions(options));
        const requestState = WebsocketStreamDataRequestState.Cancel;
        const request = <PowerShellStreamRequest>{ id, target, requestState, script: null };

        // remove from queue if not submitted yet.
        const queue = this.queues.get(target.nodeName);
        if (queue) {
            const pendingRequest = queue.pendingRequests.find(entry => entry.id === id);
            if (pendingRequest) {
                queue.pendingRequests.remove(pendingRequest);
                queue.outstandingCount--;
                const processor = this.processors.get(id);
                this.processors.delete(id);
                processor.complete();
                return;
            }
        }

        this.websocketStream.sendNext(WebsocketStreamName.PowerShellStreamName, request);
    }

    /**
     * Reset data for connection cleanup.
     */
    public reset(): void {
        Logging.log({ level: LogLevel.Warning, message: this.strings.ResetError.message, source: PowerShellStream.logSourceName });
        const processors: PowerShellProcessor[] = [];
        this.processors.forEach((value) => processors.push(value));
        this.processors.clear();
        processors.forEach((processor) => {
            processor.error(new Error(this.strings.ResetError.message));
        });
    }

    /**
     * Process the socket message.
     *
     * @param message the socket message.
     */
    public process(message: PowerShellStreamResponse): void {
        if (!message) {
            throw new Error(this.strings.NoContentError.message);
        }

        const processor = this.processors.get(message.id);
        if (!processor) {
            Logging.log({ level: LogLevel.Warning, message: this.strings.UnexpectedReceivedError.message, source: PowerShellStream.logSourceName });
            return;
        }

        switch (message.state) {
            case WebsocketStreamDataState.Data:
                this.operationNext(processor, message.response);
                break;

            case WebsocketStreamDataState.Completed:
                this.operationComplete(processor, message.response);
                this.operationEnd(message.id);
                break;

            case WebsocketStreamDataState.Error:
                this.operationError(processor, { xhr: message });
                this.operationEnd(message.id);
                break;

            case WebsocketStreamDataState.Noop:
                break;
        }
    }

    /**
     * Gets the JEA powershell endpoint, if it exists
     *
     * @param nodeName The node name
     */
    public getJeaEndpoint(nodeName: string) {
        return this.authorizationManager.getJeaEndpoint(nodeName);
    }

    /**
     * Gets websocket stream connection state.
     */
    public getWebsocketStreamConnectionState(): WebsocketStreamConnectionState {
        return this.websocketStream.socketStateRaw;
    }

    /**
     * Gets WebSocket State.
     */
    public get websocketState(): ReplaySubject<WebsocketStreamConnectionState> {
        return this.websocketStream.socketState;
    }

    private operationNext(processor: PowerShellProcessor, response: PowerShellResult): boolean {
        const partial = processor.options && processor.options.partial;

        // buffering result.
        if (!partial) {
            if (!processor.response) {
                processor.response = response;
            } else {
                if (response.errors) {
                    if (!processor.response.errors) {
                        processor.response.errors = response.errors;
                    } else {
                        response.errors.forEach(value => processor.response.errors.push(value));
                    }
                }

                if (response.progress) {
                    if (!processor.response.progress) {
                        processor.response.progress = response.progress;
                    } else {
                        response.progress.forEach(value => processor.response.progress.push(value));
                    }
                }

                if (response.results) {
                    if (!processor.response.results) {
                        processor.response.results = response.results;
                    } else {
                        response.results.forEach(value => processor.response.results.push(value));
                    }
                }
            }
        } else {
            processor.next(response);
        }

        return !partial;
    }

    private operationComplete(processor: PowerShellProcessor, response: PowerShellResult): void {
        if (this.operationNext(processor, response)) {
            processor.next(processor.response);
        }

        processor.complete();
    }

    private operationError(processor: PowerShellProcessor, error: any): void {
        processor.error(error);
    }

    private operationEnd(id: string): void {
        const processor = this.processors.get(id);
        this.processors.delete(id);
        const queue = this.queues.get(processor.target.nodeName);
        if (--queue.outstandingCount === 0) {
            this.queues.delete(processor.target.nodeName);
        }

        if (queue.pendingRequests.length > 0) {
            // if there is queued item, then send request.
            const request = queue.pendingRequests.shift();
            this.websocketStream.sendNext(WebsocketStreamName.PowerShellStreamName, request);
        }
    }

    private createRequest<T>(
        nodeName: string,
        command: PowerShellCommand,
        options?: PowerShellStreamOptions): Observable<T> {
        // publish object is created two ways.
        // 1) socket is connected so submit the request immediately with simple observable.
        //   (if-block and this is the most of cases.)
        // 2) socket is not connected so wait for the socket to ready and submit request with
        //    complex observable. Initial connect and re-connection takes this observable.
        //   (else-block and this is a few cases.)
        let publish: Observable<T>;
        const endpoint = this.authorizationManager.getJeaEndpoint(nodeName);
        const newOptions = {...(options || {})};
        if (endpoint) {
            newOptions.powerShellEndpoint = endpoint;
        }

        if (this.websocketStream.socketStateRaw === WebsocketStreamConnectionState.Connected) {
            publish = this.createRequestSimple(nodeName, command, newOptions);
        } else {
            publish = this.websocketStream.socketState
                .pipe(
                    filter(state => state === WebsocketStreamConnectionState.Connected
                        || state === WebsocketStreamConnectionState.Failed
                        || state === WebsocketStreamConnectionState.NotConfigured),
                    take(1),
                    mergeMap(state => {
                        if (state === WebsocketStreamConnectionState.Connected) {
                            return this.createRequestSimple<T>(nodeName, command, newOptions);
                        }

                        return throwError(() => new Error(this.strings.ConnectionError.message));
                    }));
        }

        return publish
            .pipe(
                catchError((error) => {
                    // retry if reset connection of socket was observed.
                    if (error && error.message === this.strings.ResetError.message) {
                        return <Observable<T>>this.monitorCreateRequest(nodeName, command, newOptions);
                    }

                    if ((!options || options.noAuth !== true) && !this.authorizationManager.signOnManager.isSignOnTokenEnabled) {
                        if (this.authorizationManager.canHandleStreamFailure(error.xhr)) {
                            return this.authorizationManager.handleStreamFailure(nodeName, options, error.xhr)
                                .pipe(switchMap(updatedOptions =>
                                    <Observable<T>>this.monitorCreateRequest(nodeName, command, updatedOptions)));
                        }
                    }

                    if (this.authorizationManager.signOnManager.isSignOnTokenEnabled) {
                        if (this.authorizationManager.signOnManager.canHandleStreamUnauthorizedLogin(error.xhr)) {
                            return this.authorizationManager.signOnManager.handleStreamUnauthorizedLogin(options, error.xhr)
                                .pipe(switchMap(updatedOptions =>
                                    <Observable<T>>this.monitorCreateRequest(nodeName, command, updatedOptions)));
                        }
                    }

                    return throwError(() => error);
                }));
    }

    private createRequestSimple<T>(
        nodeName: string,
        command: PowerShellCommand,
        options?: PowerShellStreamOptions): Observable<T> {
        return new Observable(observer => {
            const target = this.getTarget(nodeName, options);
            const requestState = WebsocketStreamDataRequestState.Normal;
            const id = this.sendRequest(observer, target, requestState, command, options);
            return () => {
                const processor = this.processors.get(id);
                if (processor) {
                    processor.end = true;
                    if (!processor.closed && !processor.closing) {
                        this.cancel(processor.target.nodeName, id);
                    }
                }
            };
        });
    }

    private sendRequest(
        observer: Observer<any>,
        target: WebsocketStreamDataTarget,
        requestState: WebsocketStreamDataRequestState,
        command: PowerShellCommand,
        options?: PowerShellStreamOptions): string {
        const id = (options && options.queryId) || MsftSme.getUniqueId();
        const request = <PowerShellStreamRequest>{
            ...{
                id,
                target,
                requestState,
                options
            },
            ...command
        };
        const processor = new PowerShellProcessor(observer, target, options);
        const queue = this.queues.get(target.nodeName);
        this.processors.set(id, processor);

        // During a send request, if caller provides 'options.close' as true,
        // we shouldn't manage the request via a queue and on Gateway, we should create a
        // new, one time use Runspace, which is disposed after use, instead of using one from the pool.
        // As currently this is not handled on Gateway, just ignore the 'options.close' for now.
        /*
        if (options && options.close) {
            // disposing session.
            this.websocketStream.sendNext(WebsocketStreamName.PowerShellStreamName, request);
            return id;
        }
        */

        if (++queue.outstandingCount > PowerShellStream.maxRunPerNode) {
            queue.pendingRequests.push(request);
            return id;
        }

        this.websocketStream.sendNext(WebsocketStreamName.PowerShellStreamName, request);
        return id;
    }

    private getTarget(nodeName: string, options: PowerShellStreamOptions): WebsocketStreamDataTarget {
        if (!this.queues.has(nodeName)) {
            const queue = <PowerShellStreamQueue>{ outstandingCount: 0, pendingRequests: [] };
            this.queues.set(nodeName, queue);
        }

        return this.websocketStream.getTarget(this.authorizationManager, nodeName, options.powerShellEndpoint);
    }

    private monitorCreateRequest<T>(
        nodeName: string,
        command: PowerShellCommand,
        options?: PowerShellStreamOptions): Observable<T> {
        let monitored: (nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions) => Observable<T>
            = (nodeName1: string, command1: PowerShellCommand, options1?: PowerShellStreamOptions) =>
                this.createRequest(nodeName1, command1, options1);
        for (const monitorSet of PowerShellStream.monitorSets) {
            monitored = this.monitor<T>(monitored, monitorSet);
        }

        return monitored(nodeName, command, options);
    }

    private monitor<T>(
        target: (nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions) => Observable<T>,
        monitorSet: PowerShellStreamMonitorSet<T>): (nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions) => Observable<T> {

        return function(nodeName: string, command: PowerShellCommand, options?: PowerShellStreamOptions): Observable<T> {
            let context: PowerShellStreamMonitorSetContext<any>;
            return monitorSet.preMonitor(nodeName, command, options)
                .pipe(
                    switchMap(packet => {
                        context = packet;
                        return target(packet.nodeName, packet.command, packet.options);
                    }),
                    catchError((error: any) => monitorSet.errorMonitor(error, context)),
                    switchMap(response => monitorSet.successMonitor(response, context))
                );
        };
    }
}
